import { Pic } from "./Pic";

import { ArrowRight, ClipboardList, Factory, HardHat } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";

const ROW_A = ["cant-gru", "hero-trave-curva", "prj-elicottero-0", "cant-trave-speciale", "prj-polivalente-cant-a", "xlam-posa", "prj-palazzetto-2", "cant-palazzetto-reticolare", "prj-cantina-1", "prj-facciate-cantiere"];
const ROW_B = ["cant-palestra-archi", "prj-auditorium-1", "stabilimento-1", "cant-teatro", "prj-polivalente-cant-b", "hero-trave-speciale", "prj-palazzetto-1", "prj-museo-2", "prj-tettomobile-1"];

const STEPS = [
  ["s0", Factory, "Ingegnerizzate in stabilimento", "Travi, pareti e connessioni vengono progettate, lavorate e preassemblate nei nostri stabilimenti: in cantiere arriva una struttura gi\u00e0 verificata, pronta da montare."],
  ["s1", ClipboardList, "Pianificazione di cantiere", "Sequenze di posa, trasporti eccezionali, mezzi di sollevamento e tempi: pianifichiamo ogni fase prima di partire, per un cantiere ordinato, sicuro e senza sorprese."],
  ["s2", HardHat, "Montaggio specializzato", "Le nostre squadre montano con autogru, piattaforme e, quando serve, anche con l'elicottero: grandi luci e geometrie complesse posate con precisione."],
];

const ask = (t) => {
  const message = t("site.msg", "Vorrei pianificare con voi la produzione e il montaggio in cantiere di una struttura in legno.");
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Lamellare curvo / elementi speciali", message } }));
  scrollToId("#contatti");
};

const Row = ({ items, reverse, t }) => (
  <div className="group flex overflow-hidden" data-testid={`construction-row-${reverse ? "b" : "a"}`}>
    <div className={`marquee-track flex shrink-0 gap-3 pr-3 group-hover:[animation-play-state:paused] ${reverse ? "marquee-reverse" : ""}`} style={{ animationDuration: "90s" }}>
      {[...items, ...items].map((n, i) => (
        <div key={`${n}-${i}`} className="h-56 w-[78vw] shrink-0 overflow-hidden sm:h-72 sm:w-[420px]" aria-hidden={i >= items.length}>
          <Pic src={`/img/emma/${n}.jpg`} alt={t("site.alt", "Cantiere di montaggio di una struttura in legno Emma Industrie")} loading="lazy" className="h-full w-full object-cover" />
        </div>
      ))}
    </div>
  </div>
);

export const Construction = () => {
  const t = useT();
  return (
    <section id="cantieri" className="relative overflow-hidden bg-[#1A1714] py-28 text-[#F3EFE6] lg:py-40" data-testid="construction-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <SectionLabel index="△" dark>{t("site.label", "Cantieri e montaggio")}</SectionLabel>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-[4.4rem]"
            lines={[t("site.h1", "Dallo stabilimento"), <span key="c">{t("site.h2a", "al")} <em className="font-serif font-normal text-[#D98A52]">{t("site.h2em", "cantiere.")}</em></span>]}
          />
          <Reveal delay={0.2} className="max-w-md text-base leading-relaxed text-[#D6D0C4] sm:text-lg lg:col-span-5">
            {t("site.intro", "Siamo specialisti nella pianificazione di cantiere e nel montaggio di strutture ingegnerizzate nei nostri stabilimenti: un unico partner dalla progettazione alla posa in opera.")}
          </Reveal>
        </div>
      </div>
      <div className="mt-16 space-y-3" data-testid="construction-gallery">
        <Row items={ROW_A} t={t} />
        <Row items={ROW_B} reverse t={t} />
      </div>
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {STEPS.map(([id, Icon, title, text], i) => (
            <Reveal key={id} delay={i * 0.1} className="border-t border-white/15 pt-6" data-testid={`construction-step-${id}`}>
              <Icon className="h-6 w-6 text-[#D98A52]" />
              <h3 className="mt-4 font-display text-xl font-bold">{t(`site.${id}.t`, title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#D6D0C4] sm:text-base">{t(`site.${id}.d`, text)}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-16">
          <button onClick={() => ask(t)} className="inline-flex items-center justify-center gap-3 rounded-full bg-[#D98A52] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#14110F] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#E9A06B]" data-testid="construction-cta">
            {t("site.cta", "Pianifica il tuo cantiere con noi")} <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
